import React, { useCallback, useEffect, useRef, useState } from 'react'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import { draggable, dropTargetForElements, monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter'
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine'
import { reorder } from '@atlaskit/pragmatic-drag-and-drop/reorder'
import { preventUnhandled } from '@atlaskit/pragmatic-drag-and-drop/prevent-unhandled'
import { attachClosestEdge, extractClosestEdge, type Edge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge'
import { getReorderDestinationIndex } from '@atlaskit/pragmatic-drag-and-drop-hitbox/util/get-reorder-destination-index'
import styles from './AgendaView.module.scss'
import { AgendaItem, AgendaItemSource } from '../../../../../models/Agenda'
import { Button } from '../../../../../components/Button/Button'

interface EditAgendaItemInputProps {
  itemId: string
  initialText: string
  onUpdate: (itemId: string, text: string) => void
  onCancel: () => void
}

const EditAgendaItemInput: React.FC<EditAgendaItemInputProps> = ({ itemId, initialText, onUpdate, onCancel }) => {
  const [text, setText] = useState(initialText)

  const handleSave = async () => {
    if (!text.trim()) {
      return
    }
    await onUpdate(itemId, text.trim())
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSave()
    }
    if (e.key === 'Escape') {
      onCancel()
    }
  }

  return (
    <div className='bg-light d-flex flex-column zocks-gap-0.75 zocks-p-0.75 rounded'>
      <div className='d-flex align-items-center zocks-gap-0.75'>
        <input
          type='text'
          className='form-control flex-grow-1 bg-white zocks-border-gray-500 paragraph-medium'
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          autoFocus
        />
        <Button
          type='button'
          variant='borderless'
          className='p-0 d-flex align-items-center justify-content-center zocks-text-gray-500'
          aria-label='Cancel'
          onClick={onCancel}
        >
          <i className='bi bi-x-lg fs-6' />
        </Button>
      </div>
      <div className='d-flex justify-content-end zocks-gap-0.5'>
        <Button type='button' className='zocks-bg-gray-200 zocks-text-gray-900 button-small zocks-px-0.5 zocks-py-0.25' variant='borderless' onClick={onCancel}>
          Cancel
        </Button>
        <Button type='button' variant='primary' className='button-small zocks-px-0.5 zocks-py-0.25' onClick={handleSave} disabled={!text.trim()}>
          Save
        </Button>
      </div>
    </div>
  )
}

interface AddAgendaItemInputProps {
  onAdd: (text: string) => void
  onCancel?: () => void
}

const AddAgendaItemInput: React.FC<AddAgendaItemInputProps> = ({ onAdd, onCancel }) => {
  const [text, setText] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!onCancel) {
      return
    }
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onCancel()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [onCancel])

  const handleAdd = () => {
    if (!text.trim()) {
      return
    }

    onAdd(text.trim())
    setText('')
    inputRef.current?.focus()
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAdd()
    }
    if (e.key === 'Escape' && onCancel) {
      onCancel()
    }
  }

  return (
    <div ref={containerRef} className='w-100 mt-2'>
      <div className='bg-light d-flex align-items-center zocks-gap-1 p-2 rounded'>
        <input
          ref={inputRef}
          type='text'
          className='form-control flex-grow-1 bg-white zocks-border-gray-500 paragraph-medium py-1 px-2'
          placeholder='Add agenda item'
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          autoFocus
        />
        <Button
          type='button'
          variant='link'
          className='text-decoration-none p-1 fw-bold paragraph-medium  flex-shrink-0'
          onClick={handleAdd}
          disabled={!text.trim()}
        >
          <span className='zocks-text-primary-dark'>Add</span>
        </Button>
      </div>
    </div>
  )
}

const EmptyAgendaState: React.FC<{ meetingTemplateId: string }> = ({ meetingTemplateId }) => (
  <li className='d-flex flex-column align-items-center justify-content-center text-center zocks-gap-0.75 py-3'>
    <div className='d-flex flex-column zocks-gap-0.5 align-items-center w-100'>
      <h6 className='mb-0 zocks-text-gray-900'>No agenda items yet</h6>
      <p className='paragraph-small mb-0 zocks-text-gray-900'>
        <span>Add a topic below, or go to </span>
        <Link
          className='text-decoration-underline zocks-text-primary'
          to={`/workspace/setup/meeting-types/meeting-type/details/${meetingTemplateId}/presets/agenda`}
        >
          meeting type settings
        </Link>
        <span> to set up fixed topics, smart suggestions, or a custom prompt.</span>
      </p>
    </div>
  </li>
)

interface AgendaItemSourceIconElementProps {
  source: AgendaItemSource
}

const AgendaItemSourceIconElement: React.FC<AgendaItemSourceIconElementProps> = ({ source }) => {
  if (source === AgendaItemSource.User) {
    return <i className='bi bi-hand-index-thumb zocks-text-gray-500' aria-label='user added' />
  }
  if (source === AgendaItemSource.Fixed) {
    return <i className='bi bi-list-ul zocks-text-gray-500' aria-label='fixed' />
  }
  if (source === AgendaItemSource.AIGenerated) {
    return <i className='bi bi-stars zocks-text-primary' aria-label='ai-generated' />
  }
  return <i className='bi bi-circle-fill' aria-label='other' />
}

interface AgendaItemSourceIconProps {
  itemId: string
  source: AgendaItemSource
}

const AgendaItemSourceIcon: React.FC<AgendaItemSourceIconProps> = ({ itemId, source }) => {
  const tooltipText =
    source === AgendaItemSource.User
      ? 'Added by User'
      : source === AgendaItemSource.Fixed
        ? 'Fixed topic from template'
        : source === AgendaItemSource.AIGenerated
          ? 'AI-generated from template'
          : null

  return tooltipText ? (
    <OverlayTrigger placement='top' overlay={<Tooltip id={`tooltip-${itemId}`}>{tooltipText}</Tooltip>}>
      <span className='me-3 d-flex align-items-center justify-content-center flex-shrink-0'>
        <AgendaItemSourceIconElement source={source} />
      </span>
    </OverlayTrigger>
  ) : (
    <span className='me-3 d-flex align-items-center justify-content-center flex-shrink-0'>
      <AgendaItemSourceIconElement source={source} />
    </span>
  )
}

interface DraggableAgendaItemProps {
  item: AgendaItem
  index: number
  isLast: boolean
  isEditing: boolean
  editingItemId: string | null
  onEdit: (id: string) => void
  onDelete: (id: string) => void
  onUpdate: (id: string, text: string) => void
  onCancelEdit: () => void
}

const DraggableAgendaItem: React.FC<DraggableAgendaItemProps> = ({
  item,
  index,
  isLast,
  isEditing,
  editingItemId,
  onEdit,
  onDelete,
  onUpdate,
  onCancelEdit,
}) => {
  const itemRef = useRef<HTMLLIElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const handleRef = useRef<HTMLSpanElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [closestEdge, setClosestEdge] = useState<Edge | null>(null)

  useEffect(() => {
    const el = itemRef.current
    const content = contentRef.current
    const handle = handleRef.current
    if (!el || !content || isEditing) {
      return
    }

    return combine(
      draggable({
        element: content,
        dragHandle: handle ?? undefined,
        getInitialData: () => ({ type: 'agenda-item', itemId: item.id, index }),
        onDragStart: () => {
          preventUnhandled.start()
          setIsDragging(true)
        },
        onDrop: () => {
          preventUnhandled.stop()
          setIsDragging(false)
        },
      }),
      dropTargetForElements({
        element: el,
        getData: ({ input, element }) =>
          attachClosestEdge({ type: 'agenda-item', itemId: item.id, index }, { input, element, allowedEdges: ['top', 'bottom'] }),
        canDrop: ({ source }) => source.data.type === 'agenda-item' && source.data.itemId !== item.id,
        onDragEnter: ({ self }) => setClosestEdge(extractClosestEdge(self.data)),
        onDrag: ({ self }) => setClosestEdge(extractClosestEdge(self.data)),
        onDragLeave: () => setClosestEdge(null),
        onDrop: () => setClosestEdge(null),
      }),
    )
  }, [item.id, index, isEditing])

  if (isEditing) {
    return (
      <li className={`position-relative ${isLast ? '' : 'mb-2'}`}>
        <EditAgendaItemInput itemId={item.id} initialText={item.text} onUpdate={onUpdate} onCancel={onCancelEdit} />
      </li>
    )
  }

  return (
    <li ref={itemRef} className={`position-relative ${isLast ? '' : 'pb-2'} ${isDragging ? 'opacity-50' : ''}`}>
      {closestEdge === 'top' && <div className={styles.dropIndicatorTop} />}
      <div ref={contentRef} className={`d-flex align-items-center bg-white zocks-border-0.25 zocks-border-gray-200 zocks-p-0.75 px-3 ${styles.agendaItem}`}>
        <span ref={handleRef} className={`d-flex align-items-center me-2 ${styles.dragHandle}`}>
          <i className='bi bi-grip-vertical zocks-text-gray-400' />
        </span>
        <AgendaItemSourceIcon itemId={item.id} source={item.source} />
        <span className='paragraph-medium flex-grow-1 zocks-text-gray-900'>{item.text}</span>
        <div className={`d-flex align-items-center zocks-gap-0.5 ms-3 ${styles.actionButtons}`}>
          <Button
            type='button'
            variant='borderless'
            className='p-1 d-flex align-items-center justify-content-center zocks-text-gray-500'
            aria-label='Edit item'
            onClick={() => onEdit(item.id)}
            disabled={editingItemId !== null}
          >
            <i className='bi bi-pencil fs-6' />
          </Button>
          <Button
            type='button'
            variant='borderless'
            className='p-1 d-flex align-items-center justify-content-center zocks-text-gray-500'
            aria-label='Delete item'
            onClick={() => onDelete(item.id)}
            disabled={editingItemId !== null}
          >
            <i className='bi bi-trash fs-6' />
          </Button>
        </div>
      </div>
      {closestEdge === 'bottom' && <div className={styles.dropIndicatorBottom} />}
    </li>
  )
}

interface UpcomingMeetingViewProps {
  items: AgendaItem[]
  meetingTemplateId: string
  onReorder: (reorderedItems: AgendaItem[]) => void
  onDelete: (agendaItemId: string) => void
  onAdd: (text: string) => void
  onEdit: (agendaItemId: string) => void
  onUpdate: (agendaItemId: string, text: string) => void
  onCancelEdit: () => void
  editingItemId: string | null
}

// Bootstrap pt-2 / pb-2 = 0.5rem = 8px at default font size.
// Used to detect whether the cursor is in the top/bottom buffer zones of the list.
const LIST_BUFFER_PX = 8

export const UpcomingMeetingView: React.FC<UpcomingMeetingViewProps> = ({
  items,
  meetingTemplateId,
  onReorder,
  onDelete,
  onAdd,
  onEdit,
  onUpdate,
  onCancelEdit,
  editingItemId,
}) => {
  const ulRef = useRef<HTMLUListElement>(null)
  const [listEdge, setListEdge] = useState<Edge | null>(null)
  const [showAddInput, setShowAddInput] = useState(false)

  const handleCancelAdd = useCallback(() => setShowAddInput(false), [])

  useEffect(() => {
    const ul = ulRef.current
    if (!ul) {
      return
    }
    return dropTargetForElements({
      element: ul,
      canDrop: ({ source }) => source.data.type === 'agenda-item',
      getData: ({ input, element }) => attachClosestEdge({ type: 'agenda-list' }, { input, element, allowedEdges: ['top', 'bottom'] }),
    })
  }, [])

  useEffect(() => {
    return monitorForElements({
      canMonitor: ({ source }) => source.data.type === 'agenda-item',
      onDrag: ({ location }) => {
        const ul = ulRef.current
        if (!ul) {
          return
        }
        const overItem = location.current.dropTargets.some((t) => t.data.type === 'agenda-item')
        if (overItem) {
          setListEdge(null)
          return
        }
        // Only show the list-level indicator when the cursor is genuinely in the pt-2/pb-2
        // buffer zones (or outside the <ul> entirely). Do NOT trigger it when the cursor
        // happens to be over the dragged item's own <li> (which has canDrop=false and so
        // never appears in dropTargets). The buffer zones are 8px = Bootstrap pt-2/pb-2.
        const { top, bottom } = ul.getBoundingClientRect()
        const clientY = location.current.input.clientY
        if (clientY < top + LIST_BUFFER_PX) {
          setListEdge('top')
        } else if (clientY > bottom - LIST_BUFFER_PX) {
          setListEdge('bottom')
        } else {
          setListEdge(null)
        }
      },
      onDrop: ({ source, location }) => {
        setListEdge(null)
        const startIndex = source.data.index as number
        const targets = location.current.dropTargets

        if (targets.length === 0) {
          // Dropped completely outside the <ul>. Use cursor Y vs list midpoint.
          const ul = ulRef.current
          if (!ul || items.length === 0) {
            return
          }
          const { top, bottom } = ul.getBoundingClientRect()
          const finishIndex = location.current.input.clientY < (top + bottom) / 2 ? 0 : items.length - 1
          if (startIndex === finishIndex) {
            return
          }
          onReorder(reorder({ list: items, startIndex, finishIndex }))
          return
        }

        const destination = targets[0]

        if (destination.data.type === 'agenda-list') {
          // Cursor is over the <ul> but not any item — could be the pt-2/pb-2 buffer zone
          // or the dragged item's own <li> (canDrop=false). Only snap to first/last when
          // the cursor is actually in the buffer zones.
          const ul = ulRef.current
          if (!ul) {
            return
          }
          const { top, bottom } = ul.getBoundingClientRect()
          const clientY = location.current.input.clientY
          let finishIndex: number
          if (clientY < top + LIST_BUFFER_PX) {
            finishIndex = 0
          } else if (clientY > bottom - LIST_BUFFER_PX) {
            finishIndex = items.length - 1
          } else {
            return
          }
          if (startIndex === finishIndex) {
            return
          }
          onReorder(reorder({ list: items, startIndex, finishIndex }))
          return
        }

        if (destination.data.type === 'agenda-item') {
          const indexOfTarget = destination.data.index as number
          const closestEdgeOfTarget = extractClosestEdge(destination.data)
          const finishIndex = getReorderDestinationIndex({ startIndex, indexOfTarget, closestEdgeOfTarget, axis: 'vertical' })
          if (startIndex === finishIndex) {
            return
          }
          onReorder(reorder({ list: items, startIndex, finishIndex }))
        }
      },
    })
  }, [items, onReorder])

  return (
    <>
      <ul ref={ulRef} className='list-unstyled mb-0 pt-2 pb-2'>
        {listEdge === 'top' ? (
          <li className='position-relative'>
            <div className={styles.dropIndicatorTop} />
          </li>
        ) : null}
        {items.length === 0 ? <EmptyAgendaState meetingTemplateId={meetingTemplateId} /> : null}
        {items.map((item, index) => (
          <DraggableAgendaItem
            key={item.id}
            item={item}
            index={index}
            isLast={index === items.length - 1}
            isEditing={editingItemId === item.id}
            editingItemId={editingItemId}
            onEdit={onEdit}
            onDelete={onDelete}
            onUpdate={onUpdate}
            onCancelEdit={onCancelEdit}
          />
        ))}
        {listEdge === 'bottom' ? (
          <li className='position-relative pb-2'>
            <div className={styles.dropIndicatorBottom} />
          </li>
        ) : null}
        {showAddInput ? <AddAgendaItemInput onAdd={onAdd} onCancel={handleCancelAdd} /> : null}
      </ul>
      {!showAddInput ? (
        <div className='zocks-mt-1.25 zocks-ms-0.5'>
          <Button variant='link' className='text-decoration-none p-0 paragraph-extra-small' onClick={() => setShowAddInput(true)}>
            <i className='bi bi-plus zocks-text-primary-dark' />
            <span className='ms-1 zocks-text-primary-dark '>{items.length > 0 ? 'Add more' : 'Add item'}</span>
          </Button>
        </div>
      ) : null}
    </>
  )
}

export default UpcomingMeetingView
